import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Lock, Unlock, Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export function MonthCloseControl({ month, year, isAdmin, onChanged }) {
  const [status, setStatus] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const token = localStorage.getItem('token');
  const qs = `token=${token}&month=${month}&year=${year}`;

  const load = () => axios.get(`${API_BASE}/api/target-achievements/month-status?${qs}`).then(r => setStatus(r.data)).catch(() => setStatus(null));
  useEffect(() => { load(); }, [month, year]);

  const toggle = async () => {
    try {
      const r = status?.is_closed
        ? await axios.delete(`${API_BASE}/api/target-achievements/close-month?${qs}`)
        : await axios.post(`${API_BASE}/api/target-achievements/close-month?${qs}`);
      toast.success(r.data.message);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'İşlem başarısız');
    } finally {
      setConfirm(false);
    }
  };

  const exportExcel = async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/api/target-achievements?${qs}`);
      const yn = (v) => (v === undefined ? '-' : v ? 'Tamamlandı' : 'Eksik');
      const rows = data.filter(a => a.has_target).map(a => ({
        'Temsilci': a.full_name, 'Kullanıcı Adı': a.username,
        'Hedef Puan': a.target_points, 'Gerçekleşen Puan': a.achieved_points,
        'Gerçekleşme %': Number(a.achievement_rate.toFixed(1)),
        'Kamera Satış': a.camera_sold ?? '-', 'Kamera Hedef': a.camera_target ?? '-', 'Kamera Durum': yn(a.camera_ok),
        'Cihaz Satış': a.device_sold ?? '-', 'Cihaz Hedef': a.device_target ?? '-', 'Cihaz Durum': yn(a.device_ok),
        'Normal Barem': a.normal_barem ?? '-', 'Uygulanan Barem': a.applied_barem ?? '-',
        'Normal Prim (TL)': Number((a.normal_commission ?? a.commission).toFixed(2)),
        'Kazanılan Prim (TL)': Number(a.commission.toFixed(2)),
        'Kaybedilen Prim (TL)': Number((a.lost_commission ?? 0).toFixed(2)),
        'Not': a.barem_note || '',
      }));
      if (!rows.length) {
        toast.error('Bu ayda hedefi olan temsilci yok');
        return;
      }
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Prim Listesi');
      XLSX.writeFile(wb, `kapali_ay_prim_${year}_${String(month).padStart(2, '0')}.xlsx`);
    } catch (e) {
      toast.error('Excel oluşturulamadı');
    }
  };

  if (!status) return null;
  const closed = status.is_closed;
  const closedText = closed ? `Ay kapatıldı (${new Date(status.closed_at).toLocaleDateString('tr-TR')} – ${status.closed_by}). Primler sabit, bu aya satış girilemez.` : null;

  return (
    <>
      {closed && (
        <div className="basis-full flex items-center gap-2 bg-gray-800 text-white text-sm px-4 py-2 rounded-lg" data-testid="month-closed-banner">
          <Lock className="w-4 h-4 shrink-0" />{closedText}
        </div>
      )}
      {isAdmin && closed && (
        <Button data-testid="closed-month-excel-btn" variant="outline" onClick={exportExcel} className="flex items-center gap-2">
          <Download className="w-4 h-4" />Prim Listesi Excel
        </Button>
      )}
      {isAdmin && (
        <Button data-testid={closed ? 'reopen-month-btn' : 'close-month-btn'} variant={closed ? 'outline' : 'default'} onClick={() => setConfirm(true)} className="flex items-center gap-2">
          {closed ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}{closed ? 'Ayı Yeniden Aç' : 'Ayı Kapat'}
        </Button>
      )}
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="month-close-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{closed ? 'Ay yeniden açılsın mı?' : `${String(month).padStart(2, '0')}/${year} kapatılsın mı?`}</AlertDialogTitle>
            <AlertDialogDescription>
              {closed
                ? 'Primler tekrar güncel ayarlar ve satışlarla canlı hesaplanacak, bu aya satış girişi açılacak.'
                : 'Tüm temsilcilerin primleri şu anki haliyle sabitlenecek ve bu aya satış girişi, düzenleme ve silme engellenecek.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="month-close-cancel-btn">Vazgeç</AlertDialogCancel>
            <AlertDialogAction data-testid="month-close-confirm-btn" onClick={toggle}>{closed ? 'Yeniden Aç' : 'Ayı Kapat'}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
